import { Component, ChangeDetectionStrategy, inject, signal, resource, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { TestToolService } from '../services/test-tool.service';
import {form, FormField, required, validateAsync} from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { TestCaseEntity } from '../models/test-case-entity';

@Component({
    selector: 'app-test-tool-test-cases-create',
    templateUrl: './test-tool-test-cases-create.component.html',
    styleUrls: ['./test-tool-test-cases-create.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormField, FormsModule]
})
export class TestToolTestCasesCreateComponent {

    private readonly loadingPanelService = inject(LoadingPanelService);
    private readonly testToolService = inject(TestToolService);
    private readonly router = inject(Router);
    private readonly toastService = inject(ToastService)

    private readonly testCaseModel = signal<TestCaseEntity>({ Id: null, Name: '', Description: '' } as TestCaseEntity);

    private readonly isTestCaseNameAvailableResource = (testCaseNameSignal: Signal<string | undefined>) => resource({
          params: () => testCaseNameSignal(),
          loader: async ({params: testCaseName}) => {
            if (!testCaseName) return undefined;
            return await this.testToolService.isTestCaseNameAvailable(testCaseName);
          },
    });

    readonly testCaseForm = form(this.testCaseModel, (schemaPath) => {
        required(schemaPath.Name, {message: 'Please enter the test case name'});
      
        validateAsync(schemaPath.Name, {
            params: ({ value }) => value(),
            factory: this.isTestCaseNameAvailableResource,
            onSuccess: (isAvailable: boolean) =>
                isAvailable
                    ? null
                    : { kind: 'unavailable', message: 'The chosen test case name is not available.' },
            onError: () => ({
                kind: 'availabilityCheckFailed',
                message: 'Could not verify whether the test case name is available.',
            }),
        });
    });

    async onSubmit(event: Event) {
        event.preventDefault();
       
        this.testCaseForm().markAsTouched();
        if (this.testCaseForm().invalid() || this.testCaseForm().pending() ) { return; }

        await this.saveTestCase(this.testCaseModel());
    }

    private async saveTestCase(testCase: TestCaseEntity): Promise<void> {
        this.loadingPanelService.showLoadingPanelDirect('Saving test case...');
        try {
            await this.testToolService.saveTestCase(testCase);
            this.toastService.showToast('Test case saved...', ToastType.success);
            await this.router.navigate(['test-tool', 'test-cases']);
        }
        catch {
            this.toastService.showToast('Failed to save test case', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();    
        }
    }

    async cancel(): Promise<void> {
        await this.router.navigate(['test-tool', 'test-cases']);
    }
}